import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import Input from "components/DesignSystem/Input/Input";
import { FormikForm } from "components/FormikForm/FormikForm";
import { US } from "constants/countryCodes";
import { useFormikContext } from "formik";
import { MultiValue, SingleValue } from "react-select";
import { Countries } from "types/Models/countries";
import { AddressPicker } from "v2/components/AddressPicker/AddressPicker";
import * as Yup from "yup";
import { LLC_TAX_OPTIONS } from "../EntityTypeStep/entityOptions";
import { ENTITY_TYPE_IDS } from "v2/constants/entityTypes";
import {
  getCountryIdForType,
  getDefaultSuffix,
  getFormationOptions,
  getPrefixesForType,
  getSelectedEntityTypeOption,
} from "./entityFieldOptions";
import { SuffixSelect } from "./SuffixSelect";

export type CompanyValues = {
  companyName: string;
  formationTypeId: string;
  taxedAs: string;
  entityName: string;
  entitySuffix: string;
  streetAddress: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  zipcode: string;
};

type PrimaryEntityFormProps = {
  countries: Countries[] | undefined;
  initialValues: CompanyValues;
  canGoBack: boolean;
  showCompanyName: boolean;
  isFormationTypeLocked: boolean;
  isSaving: boolean;
  onSave: (values: CompanyValues) => void | Promise<void>;
  onBack: () => void;
};

const TAX_OPTIONS = LLC_TAX_OPTIONS.map((option) => ({
  value: option.key,
  label: option.name,
}));

const buildSchema = (showCompanyName: boolean) =>
  Yup.object({
    companyName: showCompanyName
      ? Yup.string().trim().required("Brand name is required")
      : Yup.string(),
    formationTypeId: Yup.string().required("Entity type is required"),
    taxedAs: Yup.string().when("formationTypeId", {
      is: ENTITY_TYPE_IDS.llc,
      then: (schema) => schema.required("Tax classification is required"),
      otherwise: (schema) => schema,
    }),
    entityName: Yup.string().trim().required("Legal entity name is required"),
    streetAddress: Yup.string().trim().required("Street address is required"),
    city: Yup.string().trim().required("City is required"),
    country: Yup.string().trim().required("Country is required"),
    state: Yup.string().trim().required("State is required"),
    zipcode: Yup.string().trim().required("Zipcode is required"),
  });

const CompanyFormFields = ({
  countries,
  showCompanyName,
  isFormationTypeLocked,
  canGoBack,
  isSaving,
  onBack,
}: {
  countries: Countries[] | undefined;
  showCompanyName: boolean;
  isFormationTypeLocked: boolean;
  canGoBack: boolean;
  isSaving: boolean;
  onBack: () => void;
}) => {
  const { values, setFieldValue, isSubmitting } =
    useFormikContext<CompanyValues>();

  const prefixes = getPrefixesForType(countries, values.formationTypeId);
  const suffixOptions = prefixes.map((prefix) => prefix.prefix);
  const defaultSuffix = getDefaultSuffix(prefixes);
  const isBusy = isSubmitting || isSaving;

  const usCountryId =
    countries?.find((country) => country.code_alpha_2 === US)?.uuid ?? "";
  const formationOptions = getFormationOptions(
    countries,
    getCountryIdForType(countries, values.formationTypeId) || usCountryId
  );

  const formationValue = getSelectedEntityTypeOption(
    formationOptions,
    values.formationTypeId,
    values.taxedAs
  );
  const taxValue =
    TAX_OPTIONS.find((option) => option.value === values.taxedAs) ?? null;

  const isLlc = values.formationTypeId === ENTITY_TYPE_IDS.llc;

  const handleFormationChange = (
    option: SingleValue<OptionData> | MultiValue<OptionData>
  ) => {
    const selectedId = option && "value" in option ? String(option.value) : "";
    setFieldValue(
      "entitySuffix",
      getDefaultSuffix(getPrefixesForType(countries, selectedId))
    );
    if (selectedId !== ENTITY_TYPE_IDS.llc) setFieldValue("taxedAs", "");
  };

  const handleTaxChange = (
    option: SingleValue<OptionData> | MultiValue<OptionData>
  ) =>
    setFieldValue(
      "taxedAs",
      option && "value" in option ? String(option.value) : ""
    );

  return (
    <div className="t-flex t-flex-col t-gap-4 t-rounded-lg t-p-4 t-shadow-light-30">
      {showCompanyName && (
        <Input
          withForm
          required
          name="companyName"
          label="Brand name"
          placeholder="Acme"
        />
      )}
      <Combobox
        withForm
        required
        name="formationTypeId"
        label="Entity type"
        placeholder="Select entity type"
        isClearable={false}
        isDisabled={isFormationTypeLocked}
        menuPortalTarget={document.body}
        shouldValidateOnChange={false}
        onChange={handleFormationChange}
        value={formationValue}
        options={formationOptions}
      />
      {isLlc && !isFormationTypeLocked && (
        <Combobox
          withForm
          required
          name="taxedAs"
          label="How is your LLC taxed?"
          placeholder="Select tax classification"
          isClearable={false}
          menuPortalTarget={document.body}
          shouldValidateOnChange={false}
          onChange={handleTaxChange}
          value={taxValue}
          options={TAX_OPTIONS}
        />
      )}
      <Input
        withForm
        required
        name="entityName"
        label="Legal entity name"
        placeholder="Acme Technologies"
        rightComponent={
          suffixOptions.length ? (
            <SuffixSelect
              options={suffixOptions}
              defaultValue={defaultSuffix}
            />
          ) : undefined
        }
      />
      <AddressPicker label="Business address" />
      <div className="t-flex t-items-center t-gap-3">
        <Button
          type="submit"
          customType="primary_outlined"
          size="small"
          isLoading={isBusy}
          disabled={isBusy}
        >
          Save
        </Button>
        {canGoBack && (
          <Button
            type="button"
            customType="ghost"
            size="small"
            className="t-text-text-30 hover:t-text-text-60"
            disabled={isBusy}
            onClick={onBack}
          >
            Back to suggested data
          </Button>
        )}
      </div>
    </div>
  );
};

export const PrimaryEntityForm = ({
  countries,
  initialValues,
  canGoBack,
  showCompanyName,
  isFormationTypeLocked,
  isSaving,
  onSave,
  onBack,
}: PrimaryEntityFormProps) => {
  const handleSubmit = (values: CompanyValues) =>
    onSave({
      ...values,
      entitySuffix:
        values.entitySuffix ||
        getDefaultSuffix(getPrefixesForType(countries, values.formationTypeId)),
    });

  return (
    <FormikForm<CompanyValues>
      initialValues={initialValues}
      enableReinitialize
      validationSchema={buildSchema(showCompanyName)}
      validateOnChange={false}
      onSubmit={handleSubmit}
    >
      <CompanyFormFields
        countries={countries}
        showCompanyName={showCompanyName}
        isFormationTypeLocked={isFormationTypeLocked}
        canGoBack={canGoBack}
        isSaving={isSaving}
        onBack={onBack}
      />
    </FormikForm>
  );
};
